<template>
  <transition name="loadfade">
    <Loading v-if="isLoading"/>
  </transition>

  <div class="login-page">
    <div class="login-atmosphere" aria-hidden="true"></div>
    <div class="login-glow login-glow--one" aria-hidden="true"></div>
    <div class="login-glow login-glow--two" aria-hidden="true"></div>

    <div class="login-shell">
      <section class="login-brand-panel">
        <img
          src="../assets/DrMata/LOGO-DRMATA-Black.png"
          class="logo-img"
          alt="Dr. Mata"
        >
        <p class="brand-kicker">Clinic Admin Portal</p>
        <h1 class="login-title">DR. MATA EYE ADMIN</h1>
        <p class="login-subtitle">
          Manage clinics, audits, organizations, and system settings from one secure workspace.
        </p>
      </section>

      <section class="login-card">
        <div class="login-card__header">
          <h2 class="login-card__heading">Welcome back</h2>
          <p class="login-card__hint">Sign in with your admin credentials</p>
        </div>

        <label class="field-label" for="username">Username</label>
        <input
          id="username"
          v-model="username"
          type="text"
          class="field-input"
          placeholder="Enter username"
          autocomplete="username"
          @keyup.enter="login"
        >

        <label class="field-label" for="password">Password</label>
        <div class="password-field">
          <input
            id="password"
            v-model="password"
            type="password"
            class="field-input"
            placeholder="Enter password"
            autocomplete="current-password"
            @keyup.enter="login"
          >
          <button
            class="password-toggle"
            :class="{ active: showPassword }"
            type="button"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
            @click="togglePasswordVisibility"
          >
            <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
          </button>
        </div>

        <button type="button" class="primary-btn" @click="login">
          Sign in
        </button>

        <p v-if="error_text" class="error-text" role="alert">{{ error_text }}</p>
      </section>
    </div>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useAuthStore } from '@/stores/authStore';
  import Loading from "@/components/Loading.vue";

  const authStore = useAuthStore();
  const router = useRouter();

  const showPassword = ref(false);
  const error_text = ref('');
  const username = ref('');
  const password = ref('');
  const isLoading = ref(false);

  function togglePasswordVisibility() {
    showPassword.value = !showPassword.value;
    const passwordInput = document.getElementById('password');
    if (passwordInput) {
      passwordInput.type = showPassword.value ? 'text' : 'password';
    }
  }

  async function login() {
      isLoading.value = true;
      const login_res = await authStore.login(username.value, password.value);
      isLoading.value = false;

      await authStore.fetchUserRoles();

      if (login_res === true) {
          if (authStore.userRole === 'state_health_org') {
              router.push('/state-dashboard');
          } else if (authStore.userRole === 'clinic_admin') {
              router.push('/');
          } else if (authStore.userRole === 'admin') {
              router.push('/');
          }
      } else {
          error_text.value = login_res;
      }
  }
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  overflow: hidden;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
  background: #f6f2f9;
}

.login-atmosphere {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(145deg, #fbf8fd 0%, #f3eef8 42%, #eef1f6 100%);
  pointer-events: none;
}

.login-glow {
  position: absolute;
  border-radius: 999px;
  filter: blur(48px);
  pointer-events: none;
  opacity: 0.7;
}

.login-glow--one {
  width: 420px;
  height: 420px;
  top: -120px;
  left: -80px;
  background: rgba(186, 111, 215, 0.28);
  animation: floatGlow 10s ease-in-out infinite;
}

.login-glow--two {
  width: 380px;
  height: 380px;
  right: -60px;
  bottom: -100px;
  background: rgba(154, 87, 221, 0.18);
  animation: floatGlow 12s ease-in-out infinite reverse;
}

.login-shell {
  position: relative;
  z-index: 1;
  width: min(920px, 100%);
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 28px;
  align-items: stretch;
}

.login-brand-panel,
.login-card {
  border-radius: 22px;
  border: 1px solid rgba(110, 43, 146, 0.1);
  backdrop-filter: blur(16px);
  box-shadow:
    0 1px 2px rgba(26, 31, 46, 0.04),
    0 24px 48px rgba(110, 43, 146, 0.08);
}

.login-brand-panel {
  padding: 42px 36px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
}

.logo-img {
  width: min(168px, 48%);
  height: auto;
  display: block;
  margin-bottom: 28px;
  object-fit: contain;
}

.brand-kicker {
  margin: 0 0 10px;
  font-size: 0.72rem;
  font-weight: 650;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--primary-dark, #6e2b92);
}

.login-title {
  margin: 0;
  font-size: clamp(1.45rem, 2.4vw, 1.9rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1a1f2e;
  line-height: 1.2;
}

.login-subtitle {
  margin: 14px 0 0;
  font-size: 0.95rem;
  line-height: 1.65;
  color: #667085;
  max-width: 34ch;
}

.login-card {
  padding: 36px 32px;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-card__header {
  margin-bottom: 24px;
}

.login-card__heading {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 650;
  color: #1a1f2e;
  letter-spacing: -0.02em;
}

.login-card__hint {
  margin: 8px 0 0;
  font-size: 0.9rem;
  color: #667085;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #4a4458;
}

.field-input {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  margin-bottom: 18px;
  border: 1px solid rgba(110, 43, 146, 0.14);
  border-radius: 14px;
  background: #faf8fc;
  color: #1a1f2e;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.field-input:focus {
  border-color: rgba(186, 111, 215, 0.55);
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(186, 111, 215, 0.14);
}

.password-field {
  position: relative;
  margin-bottom: 18px;
}

.password-field .field-input {
  margin-bottom: 0;
  padding-right: 52px;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #6b7285;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.18s ease, background-color 0.18s ease;
}

.password-toggle:hover,
.password-toggle.active {
  color: var(--primary-dark, #6e2b92);
  background: rgba(186, 111, 215, 0.1);
}

.primary-btn {
  width: 100%;
  min-height: 50px;
  margin-top: 8px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--primary-color, #ba6fd7), var(--primary-dark, #6e2b92));
  color: #ffffff;
  font-size: 0.98rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  box-shadow: 0 12px 28px rgba(186, 111, 215, 0.3);
  transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
}

.primary-btn:hover {
  filter: brightness(1.03);
  transform: translateY(-1px);
  box-shadow: 0 14px 32px rgba(186, 111, 215, 0.36);
}

.primary-btn:active {
  transform: translateY(0);
}

.error-text {
  margin: 16px 0 0;
  text-align: center;
  font-size: 0.84rem;
  color: var(--error-color, #dc362e);
}

@keyframes floatGlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(18px); }
}

.loadfade-enter-active,
.loadfade-leave-active {
  transition: opacity 0.3s;
}

.loadfade-enter-from,
.loadfade-leave-to {
  opacity: 0;
}

@media (max-width: 860px) {
  .login-shell {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .login-brand-panel {
    padding: 28px 24px 22px;
    text-align: center;
    align-items: center;
  }

  .login-subtitle {
    max-width: 40ch;
  }

  .logo-img {
    margin-inline: auto;
    margin-bottom: 18px;
  }

  .login-card {
    padding: 28px 22px;
  }

  .field-input {
    font-size: 16px;
  }
}
</style>
